/**
 * Montos tecleados por el usuario: la pantalla muestra es-VE ("11.384,16") y el canal canónico es "11384.16".
 * `normalizarDecimal` acepta ambos y devuelve el canónico; si no reconoce el texto lo devuelve tal cual
 * (así el validador Zod muestra su error normal).
 */
import { Money } from '@mg/shared';

export function normalizarDecimal(texto: string): string {
  let t = texto.trim().replace(/\s|\$|Bs\.?/gi, '');
  if (t === '') return '';
  const negativo = t.startsWith('-');
  if (negativo) t = t.slice(1);
  if (!/^[\d.,]+$/.test(t)) return texto;

  const coma = t.lastIndexOf(',');
  const punto = t.lastIndexOf('.');
  let entero = t;
  let decimales = '';
  if (coma >= 0 && punto >= 0) {
    // Ambos separadores: el último es el decimal ("11.384,16" o "11,384.16")
    const sep = Math.max(coma, punto);
    entero = t.slice(0, sep);
    decimales = t.slice(sep + 1);
  } else if (coma >= 0) {
    // Solo comas: una = decimal ("1,5"); varias = miles ("1,234,567")
    if ((t.match(/,/g) ?? []).length === 1) {
      entero = t.slice(0, coma);
      decimales = t.slice(coma + 1);
    }
  } else if (punto >= 0) {
    // Solo puntos: varios = miles ("1.234.567"); uno con exactamente 3 dígitos detrás y entero ≠ 0 = miles ("11.384");
    // el resto es decimal canónico ("1234.56", "0.125")
    const puntos = (t.match(/\./g) ?? []).length;
    const detras = t.slice(punto + 1);
    const delante = t.slice(0, punto);
    if (puntos === 1 && !(detras.length === 3 && delante !== '' && delante !== '0')) {
      entero = delante;
      decimales = detras;
    }
  }
  entero = entero.replace(/[.,]/g, '');
  decimales = decimales.replace(/[.,]/g, '');
  if (entero === '' && decimales === '') return texto;
  if (entero === '') entero = '0';
  if (!/^\d+$/.test(entero) || (decimales !== '' && !/^\d+$/.test(decimales))) return texto;
  return `${negativo ? '-' : ''}${entero}${decimales ? `.${decimales}` : ''}`;
}

/** Money o null si el texto no es un monto reconocible. */
export function parseMonto(texto: string): Money | null {
  try {
    const n = normalizarDecimal(texto);
    if (n === '') return null;
    return Money.parse(n);
  } catch {
    return null;
  }
}

/**
 * Factor divisa (un multiplicador pequeño como 1,185): un solo separador con 1 o 2 dígitos delante es SIEMPRE decimal
 * ("1.185" y "1,185" = 1,185). El resto sigue la regla general.
 */
export function normalizarFactor(texto: string): string {
  const t = texto.trim();
  if (/^\d{1,2}[.,]\d+$/.test(t)) return t.replace(',', '.');
  return normalizarDecimal(t);
}
